// The design tokens, mirrored in JavaScript.
//
// assets/styles/tokens.css is the source of truth. This file exists because
// MUI v4 builds its palette in JS at theme-creation time (it computes
// contrast text and light/dark shades from the hex you give it), and because
// the `styles.js` / makeStyles files cannot read a CSS custom property
// either. So the numbers appear twice: if you change one, change the other
// (no spec checks it since 3 Nov 2026, docs/DECISIONS.md).
//
// Prefer the CSS variable. Reach for this file only when you are inside JSS
// or configuring MUI / Stripe Elements.

// ---- The :root block of tokens.css, verbatim (phone values) --------------
export const cssVars = {
  '--ink': '#1C202F',
  '--ink-hover': '#0F121C',
  '--mute': '#5B6070',
  '--paper': '#FFFFFF',
  '--stone': '#F4F2EE',
  '--line': '#E3E0DA',
  '--accent': '#0F766E',
  '--accent-soft': '#E6F2F0',
  '--error': '#B42318',
  '--star': '#D9A21B',
  '--whatsapp': '#25D366',
  '--on-ink': '#FFFFFF',
  '--on-accent': '#FFFFFF',

  '--font-display': "'Instrument Serif', Georgia, 'Times New Roman', serif",
  '--font-body': "Poppins, 'Helvetica Neue', Arial, sans-serif",

  '--fs-h1': '38px', '--lh-h1': '42px',
  '--fs-h2': '30px', '--lh-h2': '36px',
  '--fs-wordmark': '24px', '--lh-wordmark': '28px',
  '--fs-subhead': '17px', '--lh-subhead': '26px',
  '--fs-h3': '17px', '--lh-h3': '22px',
  '--fs-body': '16px', '--lh-body': '24px',
  '--fs-price': '20px', '--lh-price': '24px',
  '--fs-total': '24px', '--lh-total': '28px',
  '--fs-button': '16px', '--lh-button': '20px',
  '--fs-label': '14px', '--lh-label': '20px',
  '--fs-input': '16px', '--lh-input': '24px',
  '--fs-caption': '13px', '--lh-caption': '18px',
  '--fs-eyebrow': '12px', '--lh-eyebrow': '16px',
  '--fs-helper': '13px', '--lh-helper': '18px',

  '--tracking-h1': '-0.01em',
  '--tracking-eyebrow': '0.06em',

  '--weight-regular': '400',
  '--weight-medium': '500',
  '--weight-semibold': '600',

  '--space-4': '4px',
  '--space-8': '8px',
  '--space-12': '12px',
  '--space-16': '16px',
  '--space-20': '20px',
  '--space-24': '24px',
  '--space-32': '32px',
  '--space-48': '48px',
  '--space-64': '64px',
  '--space-96': '96px',

  '--section-gap': '64px',
  '--section-pad': '32px',
  '--heading-to-intro': '12px',
  '--intro-to-content': '32px',

  '--container-page': '1200px',
  '--container-read': '640px',
  '--container-form': '640px',
  '--container-summary': '360px',
  '--gutter': '20px',
  '--grid-gap': '16px',

  '--radius-card': '12px',
  '--radius-control': '10px',
  '--radius-hero': '16px',
  '--radius-photo': '12px',
  '--radius-pill': '999px',

  '--shadow-max': '0 1px 2px rgba(28, 32, 47, 0.06)',

  '--control-h': '56px',
  '--tap-min': '44px',
  '--header-h': '56px',
  '--booking-bar-h': '56px',

  '--focus-width': '2px',
  '--focus-offset': '2px',
  '--focus-color': 'var(--accent)',

  '--motion-fast': '120ms',
  '--motion': '180ms',
  '--ease': 'cubic-bezier(0.2, 0.6, 0.2, 1)'
}

// ---- The `@media (min-width: 960px)` override block, verbatim ------------
export const cssVarsDesktop = {
  '--fs-h1': '64px', '--lh-h1': '68px',
  '--fs-h2': '40px', '--lh-h2': '46px',
  '--fs-wordmark': '28px', '--lh-wordmark': '32px',
  '--fs-subhead': '20px', '--lh-subhead': '30px',
  '--fs-h3': '18px', '--lh-h3': '24px',
  '--fs-body': '16px', '--lh-body': '26px',
  '--fs-price': '24px', '--lh-price': '28px',
  '--fs-total': '28px', '--lh-total': '32px',
  '--fs-caption': '14px', '--lh-caption': '20px',
  '--fs-eyebrow': '13px', '--lh-eyebrow': '18px',

  '--section-gap': '96px',
  '--section-pad': '48px',

  '--gutter': '24px',
  '--grid-gap': '24px',

  '--control-h': '52px',
  '--header-h': '72px',
  '--booking-bar-h': '64px'
}

const px = name => parseInt(cssVars[name], 10)
const pxDesktop = name => parseInt(cssVarsDesktop[name] || cssVars[name], 10)

// ---- Convenience views, for JSS and for Stripe `appearance` --------------

export const colors = {
  ink: cssVars['--ink'],
  inkHover: cssVars['--ink-hover'],
  mute: cssVars['--mute'],
  paper: cssVars['--paper'],
  stone: cssVars['--stone'],
  line: cssVars['--line'],
  accent: cssVars['--accent'],
  accentSoft: cssVars['--accent-soft'],
  error: cssVars['--error'],
  star: cssVars['--star'],
  whatsapp: cssVars['--whatsapp'],
  onInk: cssVars['--on-ink'],
  onAccent: cssVars['--on-accent']
}

export const fonts = {
  display: cssVars['--font-display'],
  body: cssVars['--font-body']
}

// space(24) -> 24. Throws on a value that is not on the scale, which is the
// point: the scale is 4, 8, 12, 16, 20, 24, 32, 48, 64, 96 and nothing else.
export const space = n => {
  const value = cssVars[`--space-${n}`]
  if (!value) throw new Error(`space(${n}) is not on the spacing scale (4, 8, 12, 16, 20, 24, 32, 48, 64, 96)`)
  return parseInt(value, 10)
}

export const radius = {
  card: px('--radius-card'),
  control: px('--radius-control'),
  hero: px('--radius-hero'),
  photo: px('--radius-photo'),
  pill: px('--radius-pill')
}

export const shadow = cssVars['--shadow-max']

// MUI v4 defaults, which the existing `Hidden` and `theme.breakpoints` calls
// already assume. 960 is the one that matters: it is where the page changes
// shape.
export const breakpoints = { sm: 600, md: 960, lg: 1280, xl: 1920 }

export const motion = {
  fast: cssVars['--motion-fast'],
  base: cssVars['--motion'],
  ease: cssVars['--ease']
}

export const sizes = {
  controlPhone: px('--control-h'),
  control: pxDesktop('--control-h'),
  tapMin: px('--tap-min'),
  headerPhone: px('--header-h'),
  header: pxDesktop('--header-h'),
  bookingBarPhone: px('--booking-bar-h'),
  bookingBar: pxDesktop('--booking-bar-h'),
  containerPage: px('--container-page'),
  containerRead: px('--container-read'),
  containerForm: px('--container-form'),
  containerSummary: px('--container-summary')
}

// A 2px accent ring at 2px offset, the one focus treatment on the site.
export const focusRing = {
  outline: `${cssVars['--focus-width']} solid ${colors.accent}`,
  outlineOffset: cssVars['--focus-offset']
}

export const tokens = { colors, fonts, space, radius, shadow, breakpoints, motion, sizes, focusRing }
